import { useCallback } from 'react';
import {
    useConnection,
    useWriteContract,
    usePublicClient,
} from 'wagmi';
import { parseUnits } from 'viem';
import {
    DEFAULT_ERROR_HEADING,
    GAS_FEES_ERROR,
} from '../../utils/ConstantMessages';
import { notifyError } from '../../components/Toast';
import BalanceOfAbi from '../../utils/BalanceOfAbi.json';

export const useTransfer = () => {
    const { address: account, isConnected } = useConnection();
    const publicClient = usePublicClient();
    const transfer = useWriteContract();

    const transferFunds = useCallback(
        async (toAddress, amount, tokenConfig) => {
            try {
                if (!isConnected || !account) {
                    notifyError('Wallet not connected');
                    return false;
                }

                const tokenAmount = parseUnits(
                    amount.toString(),
                    tokenConfig.tokenDecimals
                );

                const gasPrice = await publicClient.getGasPrice();
                const adjustedGasPrice = gasPrice * 2n;

                const gasEstimate = await publicClient.estimateContractGas({
                    address: tokenConfig.tokenAddress,
                    abi: BalanceOfAbi,
                    functionName: 'transfer',
                    args: [toAddress, tokenAmount],
                    account,
                });

                const totalGasCost = gasEstimate * adjustedGasPrice;
                const nativeBalance = await publicClient.getBalance({
                    address: account,
                });

                if (nativeBalance < totalGasCost) {
                    notifyError(DEFAULT_ERROR_HEADING, GAS_FEES_ERROR);
                    return false;
                }

                const hash = await transfer.mutateAsync({
                    address: tokenConfig.tokenAddress,
                    abi: BalanceOfAbi,
                    functionName: 'transfer',
                    args: [toAddress, tokenAmount],
                    gas: gasEstimate,
                    gasPrice: adjustedGasPrice,
                });

                return { transactionHash: hash, hash };
            } catch (error) {
                const isUserRejected =
                    error?.name === 'UserRejectedRequestError' ||
                    error?.shortMessage?.toLowerCase().includes('user rejected') ||
                    error?.message?.toLowerCase().includes('user denied');
                if (isUserRejected) {
                    notifyError('Transaction Cancelled', 'You rejected the transaction.');
                    return false;
                }
                if (error?.message?.includes('transfer amount exceeds balance')) {
                    notifyError('Insufficient Balance', 'You don\'t have enough tokens for this transfer.');
                    return false;
                }
                if (error?.message?.includes('transfer amount exceeds allowance')) {
                    notifyError('Insufficient Allowance', 'Please approve the token spend first.');
                    return false;
                }

                if (error?.message?.includes('reverted')) {
                    const reasonMatch = error.message.match(/reason: (.+?)(?:\n|$)/);
                    const reason = reasonMatch ? reasonMatch[1] : 'Contract execution failed';
                    notifyError('Transaction Failed', reason);
                    return false;
                }

                notifyError('Transaction Failed', error?.shortMessage || 'An unexpected error occurred');
                return false;
            }
        },
        [account, isConnected, publicClient, transfer]
    );

    return transferFunds;
};
